import React from 'react';

import type { CSSProperties } from 'react';

import { lerp } from '@utils/animation';

/** Size of the SVG viewBox */
const VIEWBOX_SIZE = 24;
/** stroke width of the progress circle, relative to the VIEWBOX_SIZE */
const STROKE = 4;
/** radius of stroke offset */
const STROKE_OFFSET = STROKE / 2;
/** center of viewbox */
const CENTER = VIEWBOX_SIZE / 2;
/** radius of circle, accounting for stroke width */
const RADIUS = VIEWBOX_SIZE / 2 - STROKE_OFFSET;
/** distance around circle */
const DISTANCE = RADIUS * 2 * Math.PI;

const ANIMATION_DURATION = 250;

interface ProgressCircleProps {
  /** number between 0...100 */
  percent: number;
  /** color to use for filled part */
  usedColor?: string;
  /** color to use for part that will be filled */
  remainingColor?: string;
  /** color to use for background inside circle */
  bgColor?: string;
  size?: number;
}

const MIN_PERCENT = 1; // looks like ~5% due to the round linecap
const MAX_PERCENT = 96; // looks like 100% due to the round linecap

export default function ProgressCircle(props: ProgressCircleProps) {
  const {
    usedColor = 'currentColor',
    remainingColor = '#F5F5F5',
    bgColor = 'transparent',
    percent,
    size = 16,
  } = props;

  // Prevents the circle from being completely filled or empty.
  const clampedPercent = lerp(MIN_PERCENT, MAX_PERCENT, percent / 100);
  const usedPercent = DISTANCE + (clampedPercent / 100) * DISTANCE;

  return (
    <svg
      className="block -rotate-90 overflow-visible"
      height={size}
      width={size}
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      style={{ display: 'block', overflow: 'visible', fill: 'none' }}
    >
      {/* Background circle */}
      <circle
        r={RADIUS}
        cx={CENTER}
        cy={CENTER}
        style={{
          fill: bgColor,
        }}
      />
      {/* Area that progress circle will fill */}
      <Progress
        color={remainingColor}
        r={RADIUS}
        style={{
          // Stroke is marginally smaller to prevent color bleeding / edge artifacts
          strokeWidth: STROKE * 0.9,
        }}
      />
      {/* Progress circle */}
      <Progress
        color={usedColor}
        r={RADIUS}
        style={{
          strokeWidth: STROKE,
          strokeDasharray: `${DISTANCE} ${DISTANCE}`,
          strokeDashoffset: usedPercent,
        }}
      />
    </svg>
  );
}

const Progress = (props: { color: string; r: number; style?: CSSProperties }) => {
  return (
    <circle
      className="linecap-[round] fill-[transparent]"
      cx={CENTER}
      cy={CENTER}
      r={props.r}
      style={{
        ...props.style,
        stroke: props.color,
        strokeLinecap: 'round',
        transition: `stroke-dashoffset ${ANIMATION_DURATION}ms linear`,
      }}
    />
  );
};
